// src/pages/LatecomerAnalytics.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";
import { toast } from "react-toastify";
import API from "../../services/API";
import {
  Search,
  Clock,
  Calendar,
  Users,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Filter,
  RefreshCw,
} from "lucide-react";

const GOV_NAVY = "#07172F";
const GOV_BLUE = "#0B4C9C";
const GOV_BLUE_2 = "#0F65C9";
const GOV_GOLD = "#C8A24A";
const GOV_BG = "#F5F9FF";

const now = new Date();
const CURRENT_MONTH = now.toISOString().slice(0, 7);
const EXCLUDED_DATE = "2026-01-29";

function shortenName(fullName = "") {
  if (!fullName) return "";

  const blacklist = [
    "MUHAMMAD",
    "MOHAMAD",
    "MOHD",
    "AHMAD",
    "NUR",
    "BIN",
    "BINTI",
    "NOR",
    "WAN",
    "SITI",
  ];

  const parts = fullName
    .toUpperCase()
    .split(/\s+/)
    .filter((p) => p && !blacklist.includes(p));

  return parts.slice(0, 2).join(" ");
}

function formatDisplayDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function LatecomerBarChart({ data }) {
  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.count - a.count)
      .slice(0, 20)
      .map((s) => ({
        name: shortenName(s.student_name),
        fullName: s.student_name,
        count: s.count,
        class_name: s.class_name,
        session: s.session,
      }));
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="spgov-empty-state">
        <div className="spgov-empty-icon">
          <Clock size={22} />
        </div>
        <strong>No late attendance trend available</strong>
        <span>There are no matching records for the selected filter.</span>
      </div>
    );
  }

  return (
    <div className="spgov-chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 12, right: 34, left: 44, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal stroke="#E5EDF7" />
          <XAxis
            type="number"
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#64748B" }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={145}
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fontWeight: 600,
              fill: "#0F172A",
            }}
          />
          <Tooltip
            cursor={{ fill: "rgba(11, 76, 156, 0.06)" }}
            formatter={(value, name, props) => [
              `${value} late check-ins`,
              `${props.payload.fullName} (${props.payload.class_name})`,
            ]}
            contentStyle={{
              border: "0",
              borderRadius: 16,
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
              fontSize: 13,
            }}
          />

          <Bar dataKey="count" fill={GOV_BLUE} radius={[0, 12, 12, 0]} barSize={22}>
            <LabelList
              dataKey="count"
              position="right"
              fill="#0F172A"
              fontSize={12}
              fontWeight={700}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function LatecomerAnalytics() {
  const [latecomers, setLatecomers] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [lateCutoff, setLateCutoff] = useState("08:00");
  const [availableSessions, setAvailableSessions] = useState(["Morning"]);

  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});
  const [loadingStudentId, setLoadingStudentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const formatTo12h = (value) => {
    const [hStr = "", mStr = "00"] = String(value || "").split(":");
    const hNum = Number(hStr);
    const mNum = Number(mStr);

    if (!Number.isFinite(hNum) || hNum < 0 || hNum > 23) {
      return value || "08:00";
    }

    const suffix = hNum >= 12 ? "PM" : "AM";
    const hour12 = hNum % 12 === 0 ? 12 : hNum % 12;
    const mm = String(Number.isFinite(mNum) ? mNum : 0).padStart(2, "0");

    return `${hour12}:${mm} ${suffix}`;
  };

  const fetchLateConfig = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: localStorage.getItem("token"),
      };

      const res = await API.get("/portal/attendance/lateConfig", { headers });

      if (res?.data?.response_code === 1000) {
        const v = res.data?.data?.late_checkin;
        if (typeof v === "string" && v) setLateCutoff(v);
      }
    } catch (_) {
      // silent
    }
  }, []);

  const fetchLatecomers = useCallback(
    async (
      pageNum = 1,
      month = selectedMonth,
      session = sessionFilter,
      name = searchQuery
    ) => {
      setLoading(true);

      try {
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          token: localStorage.getItem("token"),
        };

        const res = await API.get("/analytics/attendance/latecomers", {
          headers,
          params: {
            is_portal: true,
            month,
            page: pageNum,
            per_page: perPage,
            session: session !== "all" ? session : undefined,
            name: name || undefined,
          },
        });

        if (res?.data?.response_code === 1000) {
          const newData = Array.isArray(res.data.data) ? res.data.data : [];
          const pagination = res.data.pagination || {};

          setAvailableSessions(res.data.available_sessions || ["Morning"]);

          setLatecomers((prev) => {
            if (pageNum === 1) return newData;

            const merged = [...prev, ...newData];
            const seen = new Set();

            return merged.filter((item) => {
              const key = item.student_id || `${item.student_name}-${item.class_name}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          });

          setPage(pageNum);
          setTotalPages(pagination.total_pages || 1);
          setTotalCount(pagination.total || newData.length);
        } else {
          toast.error(res?.data?.message || "Failed to fetch late attendance records", {
            autoClose: 5000,
          });
        }
      } catch (err) {
        toast.error(err?.message || "Failed to fetch late attendance records", {
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [perPage, selectedMonth, sessionFilter, searchQuery]
  );

  const fetchStudentLateDetails = async (studentId) => {
    setLoadingStudentId(studentId);

    try {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: localStorage.getItem("token"),
      };

      const res = await API.get(`/analytics/attendance/latecomers/${studentId}`, {
        headers,
        params: { month: selectedMonth, is_portal: true },
      });

      if (res?.data?.response_code === 1000) {
        setStudentDetails((prev) => ({
          ...prev,
          [studentId]: res.data.data || [],
        }));
        setExpandedStudentId(studentId);
      } else {
        toast.error(res?.data?.message || "Failed to fetch late attendance details");
      }
    } catch (err) {
      toast.error(err?.message || "Error fetching late attendance details");
    } finally {
      setLoadingStudentId(null);
    }
  };

  const handleRowClick = (studentId) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
    } else if (studentDetails[studentId]) {
      setExpandedStudentId(studentId);
    } else {
      fetchStudentLateDetails(studentId);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setInitialLoading(true);
      fetchLatecomers(1, selectedMonth, sessionFilter, searchQuery);
    }, 400);

    return () => clearTimeout(handler);
  }, [selectedMonth, sessionFilter, searchQuery, fetchLatecomers]);

  useEffect(() => {
    fetchLateConfig();
  }, [fetchLateConfig]);

  const latecomerSummary = useMemo(
    () =>
      (latecomers || []).map((l) => ({
        student_id: l.student_id,
        student_name: l.student_name,
        class_name: l.class_name || "Unknown",
        session: l.session || "Morning",
        profile_pic: l.profile_pic,
        count: Number(l.late_count) || 0,
        last_date: l.last_date,
        last_checkin: l.last_checkin,
      })),
    [latecomers]
  );

  const filteredLatecomers = useMemo(() => {
    let list = latecomerSummary;

    list = list.filter((s) => {
      if (!s.last_date) return true;
      return s.last_date !== EXCLUDED_DATE;
    });

    if (sessionFilter !== "all") {
      list = list.filter(
        (s) => (s.session || "").toLowerCase() === sessionFilter.toLowerCase()
      );
    }

    return list;
  }, [latecomerSummary, sessionFilter]);

  const totalLateIncidents = useMemo(
    () => filteredLatecomers.reduce((sum, item) => sum + Number(item.count || 0), 0),
    [filteredLatecomers]
  );

  const highestLateCount = useMemo(() => {
    if (!filteredLatecomers.length) return 0;
    return Math.max(...filteredLatecomers.map((item) => Number(item.count || 0)));
  }, [filteredLatecomers]);

  const uniqueDepartments = useMemo(() => {
    return new Set(filteredLatecomers.map((item) => item.class_name).filter(Boolean)).size;
  }, [filteredLatecomers]);

  return (
    <div className="spgov-late-page">
      <style>{`
        .spgov-late-page {
          min-height: 100vh;
          padding: 28px;
          background:
            radial-gradient(circle at top left, rgba(11, 76, 156, 0.10), transparent 28%),
            radial-gradient(circle at top right, rgba(7, 23, 47, 0.08), transparent 26%),
            linear-gradient(180deg, ${GOV_BG} 0%, #eef4fb 100%);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: #0f172a;
        }

        .spgov-late-hero {
          position: relative;
          overflow: hidden;
          margin-bottom: 20px;
          padding: 28px;
          border-radius: 28px;
          color: #ffffff;
          background:
            linear-gradient(135deg, rgba(7, 23, 47, 0.98), rgba(11, 76, 156, 0.94)),
            radial-gradient(circle at 88% 12%, rgba(59, 130, 246, 0.28), transparent 34%);
          box-shadow: 0 26px 70px rgba(8, 27, 58, 0.18);
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 24px;
          align-items: center;
        }

        .spgov-late-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 44px 44px;
          pointer-events: none;
        }

        .spgov-late-hero > * {
          position: relative;
          z-index: 2;
        }

        .spgov-late-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: fit-content;
          margin-bottom: 12px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.09);
          color: rgba(255,255,255,0.86);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .spgov-late-hero h1 {
          margin: 0;
          font-size: clamp(30px, 4vw, 46px);
          line-height: 1.05;
          font-weight: 760;
          letter-spacing: -0.03em;
        }

        .spgov-late-hero p {
          max-width: 780px;
          margin: 14px 0 0;
          color: rgba(255,255,255,0.74);
          font-size: 14.5px;
          line-height: 1.65;
          font-weight: 400;
        }

        .spgov-late-cutoff {
          min-width: 230px;
          padding: 18px;
          border-radius: 22px;
          border: 1px solid rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.11);
          backdrop-filter: blur(12px);
          box-shadow: 0 18px 40px rgba(0,0,0,0.12);
        }

        .spgov-late-cutoff span {
          display: block;
          color: rgba(255,255,255,0.70);
          font-size: 12px;
          font-weight: 600;
        }

        .spgov-late-cutoff strong {
          display: block;
          margin-top: 6px;
          color: #ffffff;
          font-size: 26px;
          font-weight: 750;
          letter-spacing: -0.02em;
        }

        .spgov-stat-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 20px;
        }

        .spgov-stat-card {
          padding: 18px;
          border-radius: 22px;
          background: rgba(255,255,255,0.94);
          border: 1px solid #dbeafe;
          box-shadow: 0 16px 42px rgba(8, 27, 58, 0.07);
        }

        .spgov-stat-card span {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
        }

        .spgov-stat-card strong {
          display: block;
          margin-top: 8px;
          color: ${GOV_NAVY};
          font-size: 28px;
          line-height: 1;
          font-weight: 750;
          letter-spacing: -0.025em;
        }

        .spgov-panel {
          border-radius: 26px;
          background: rgba(255,255,255,0.96);
          border: 1px solid #dbeafe;
          box-shadow: 0 18px 48px rgba(8, 27, 58, 0.08);
          overflow: hidden;
          margin-bottom: 20px;
        }

        .spgov-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 22px 24px;
          border-bottom: 1px solid #e5edf7;
          background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
        }

        .spgov-panel-header h3 {
          margin: 0;
          color: ${GOV_NAVY};
          font-size: 20px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .spgov-panel-header p {
          margin: 5px 0 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 400;
        }

        .spgov-filter-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }

        .spgov-control {
          height: 42px;
          min-width: 170px;
          border-radius: 14px;
          border: 1px solid #dbeafe;
          background: #ffffff;
          color: #0f172a;
          font-size: 13px;
          font-weight: 500;
          box-shadow: none;
          outline: none;
          padding: 0 13px;
        }

        .spgov-search-wrap {
          position: relative;
        }

        .spgov-search-wrap svg {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .spgov-search-wrap input {
          padding-left: 38px;
        }

        .spgov-chart-wrap {
          width: 100%;
          height: 430px;
          padding: 18px 18px 10px;
        }

        .spgov-table-wrap {
          overflow: auto;
        }

        .spgov-table {
          width: 100%;
          min-width: 920px;
          border-collapse: separate;
          border-spacing: 0;
          margin: 0;
        }

        .spgov-table thead th {
          padding: 15px 18px;
          background: #f5f9ff;
          color: ${GOV_BLUE};
          border-bottom: 1px solid #dbeafe;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .spgov-table tbody td {
          padding: 16px 18px;
          border-bottom: 1px solid #eef2f7;
          color: #0f172a;
          font-size: 14px;
          vertical-align: middle;
        }

        .spgov-table tbody tr:hover {
          background: #eff6ff;
        }

        .spgov-person-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .spgov-avatar {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: #eef6ff;
          color: ${GOV_BLUE};
          font-weight: 700;
          flex: 0 0 42px;
        }

        .spgov-person-name {
          color: ${GOV_NAVY};
          font-weight: 650;
        }

        .spgov-person-id {
          margin-top: 2px;
          color: #64748b;
          font-size: 12px;
        }

        .spgov-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 28px;
          padding: 5px 10px;
          border-radius: 999px;
          background: #eef6ff;
          color: ${GOV_BLUE};
          font-size: 12px;
          font-weight: 650;
        }

        .spgov-badge.warning {
          background: #fff7ed;
          color: #9a3412;
        }

        .spgov-expanded-row {
          background: #ffffff;
        }

        .spgov-detail-box {
          margin: 0;
          padding: 14px;
          border-radius: 18px;
          background: #f8fbff;
          border: 1px solid #dbeafe;
        }

        .spgov-detail-table {
          width: 100%;
          border-collapse: collapse;
        }

        .spgov-detail-table th {
          padding: 10px 12px;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
          border-bottom: 1px solid #e2e8f0;
        }

        .spgov-detail-table td {
          padding: 10px 12px;
          color: #0f172a;
          font-size: 13px;
          border-bottom: 1px solid #eef2f7;
        }

        .spgov-load-more {
          padding: 18px;
          text-align: center;
          border-top: 1px solid #eef2f7;
        }

        .spgov-load-more button {
          border: none;
          border-radius: 999px;
          padding: 10px 22px;
          background: ${GOV_BLUE};
          color: #ffffff;
          font-size: 13px;
          font-weight: 650;
          box-shadow: 0 12px 26px rgba(11, 76, 156, 0.22);
        }

        .spgov-empty-state {
          min-height: 260px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 10px;
          text-align: center;
          color: #64748b;
        }

        .spgov-empty-icon {
          width: 48px;
          height: 48px;
          border-radius: 16px;
          display: grid;
          place-items: center;
          background: #eef6ff;
          color: ${GOV_BLUE};
          border: 1px solid #dbeafe;
        }

        .spgov-empty-state strong {
          color: ${GOV_NAVY};
          font-size: 16px;
          font-weight: 700;
        }

        .spgov-empty-state span {
          font-size: 13px;
        }

        .spgov-loading {
          min-height: 360px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 12px;
          color: #64748b;
        }

        @media (max-width: 992px) {
          .spgov-late-page {
            padding: 18px;
          }

          .spgov-late-hero {
            grid-template-columns: 1fr;
          }

          .spgov-late-cutoff {
            min-width: 0;
          }

          .spgov-stat-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .spgov-panel-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .spgov-filter-row,
          .spgov-control,
          .spgov-search-wrap {
            width: 100%;
          }

          .spgov-search-wrap input {
            width: 100%;
          }
        }

        @media (max-width: 576px) {
          .spgov-late-page {
            padding: 12px;
          }

          .spgov-late-hero {
            padding: 20px;
            border-radius: 22px;
          }

          .spgov-late-hero h1 {
            font-size: 26px;
          }

          .spgov-stat-grid {
            grid-template-columns: 1fr;
          }

          .spgov-panel-header {
            padding: 18px;
          }

          .spgov-chart-wrap {
            height: 380px;
            padding: 10px;
          }
        }
      `}</style>

      <section className="spgov-late-hero">
        <div>
          <div className="spgov-late-kicker">
            <ShieldCheck size={14} />
            SPGO.my Attendance Compliance
          </div>

          <h1>Late Attendance Analytics</h1>

          <p>
            Monitor personnel late check-ins, department attendance risk,
            recurring late patterns and detailed attendance records for the
            selected reporting month.
          </p>
        </div>

        <div className="spgov-late-cutoff">
          <span>Late Check-In Threshold</span>
          <strong>{formatTo12h(lateCutoff)} MYT</strong>
        </div>
      </section>

      <section className="spgov-stat-grid">
        <div className="spgov-stat-card">
          <span>
            <Users size={15} />
            Personnel Records
          </span>
          <strong>{filteredLatecomers.length}</strong>
        </div>

        <div className="spgov-stat-card">
          <span>
            <AlertTriangle size={15} />
            Late Incidents
          </span>
          <strong>{totalLateIncidents}</strong>
        </div>

        <div className="spgov-stat-card">
          <span>
            <Clock size={15} />
            Highest Count
          </span>
          <strong>{highestLateCount}</strong>
        </div>

        <div className="spgov-stat-card">
          <span>
            <Filter size={15} />
            Departments
          </span>
          <strong>{uniqueDepartments}</strong>
        </div>
      </section>

      <section className="spgov-panel">
        <div className="spgov-panel-header">
          <div>
            <h3>Late Attendance Overview</h3>
            <p>
              Top personnel ranked by late attendance count for the selected
              reporting period.
            </p>
          </div>

          <div className="spgov-filter-row">
            <div className="spgov-search-wrap">
              <Search size={15} />
              <input
                type="text"
                className="spgov-control"
                placeholder="Search personnel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="spgov-control"
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
            >
              <option value="all">All Sessions</option>
              {availableSessions.map((s) => (
                <option key={s.toLowerCase()} value={s.toLowerCase()}>
                  {s}
                </option>
              ))}
            </select>

            <input
              type="month"
              className="spgov-control"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
        </div>

        {initialLoading ? (
          <div className="spgov-loading">
            <div className="spinner-border" role="status" />
            <span>Loading late attendance records...</span>
          </div>
        ) : (
          <LatecomerBarChart data={filteredLatecomers} />
        )}
      </section>

      <section className="spgov-panel">
        <div className="spgov-panel-header">
          <div>
            <h3>Personnel Late Attendance Records</h3>
            <p>
              Click a row to view detailed late check-in history for the
              selected month.
            </p>
          </div>

          <div className="spgov-filter-row">
            <button
              className="spgov-control"
              type="button"
              onClick={() => fetchLatecomers(1, selectedMonth, sessionFilter, searchQuery)}
              disabled={loading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                color: GOV_BLUE,
                fontWeight: 650,
              }}
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>
        </div>

        <div className="spgov-table-wrap">
          <table className="spgov-table">
            <thead>
              <tr>
                <th style={{ minWidth: 260 }}>Personnel</th>
                <th className="text-center">Department</th>
                <th className="text-center">Times Late</th>
                <th className="text-center">Last Late Date</th>
                <th className="text-center">Last Check-In</th>
                <th className="text-center">Session</th>
                <th className="text-center">Details</th>
              </tr>
            </thead>

            <tbody>
              {filteredLatecomers.length > 0 ? (
                filteredLatecomers.map((s) => {
                  const isExpanded = expandedStudentId === s.student_id;

                  return (
                    <React.Fragment key={s.student_id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(s.student_id)}
                      >
                        <td>
                          <div className="spgov-person-cell">
                            <div className="spgov-avatar">
                              {(s.student_name || s.student_id || "P")
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <div className="spgov-person-name">
                                {s.student_name || s.student_id}
                              </div>
                              <div className="spgov-person-id">{s.student_id}</div>
                            </div>
                          </div>
                        </td>

                        <td className="text-center">
                          <span className="spgov-badge">{s.class_name}</span>
                        </td>

                        <td className="text-center">
                          <span className="spgov-badge warning">{s.count}</span>
                        </td>

                        <td className="text-center">
                          {formatDisplayDate(s.last_date)}
                        </td>

                        <td className="text-center" style={{ color: GOV_BLUE, fontWeight: 650 }}>
                          {s.last_checkin || "-"}
                        </td>

                        <td className="text-center">{s.session}</td>

                        <td className="text-center">
                          {isExpanded ? (
                            <ChevronUp size={18} color={GOV_BLUE} />
                          ) : (
                            <ChevronDown size={18} color="#64748B" />
                          )}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="spgov-expanded-row">
                          <td colSpan={7}>
                            <div className="spgov-detail-box">
                              {loadingStudentId === s.student_id ? (
                                <div className="p-3 text-center text-muted">
                                  <div className="spinner-border spinner-border-sm me-2" />
                                  Loading detailed records...
                                </div>
                              ) : studentDetails[s.student_id]?.length > 0 ? (
                                <table className="spgov-detail-table">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Check-In</th>
                                      <th>Session</th>
                                      <th>Department</th>
                                      <th>Remarks</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {studentDetails[s.student_id].map((r, idx) => (
                                      <tr key={`${r.date}-${idx}`}>
                                        <td>{formatDisplayDate(r.date)}</td>
                                        <td>{r.checkin_time || "-"}</td>
                                        <td>{r.session || "-"}</td>
                                        <td>{r.class_name || "-"}</td>
                                        <td>{r.remarks || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="p-3 text-center text-muted">
                                  No detailed late attendance records found.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="spgov-empty-state">
                      <div className="spgov-empty-icon">
                        <Clock size={22} />
                      </div>
                      <strong>No late attendance records found</strong>
                      <span>
                        Try changing the month, session or search filter.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {page < totalPages && (
          <div className="spgov-load-more">
            <button
              disabled={loading}
              onClick={() => fetchLatecomers(page + 1, selectedMonth, sessionFilter)}
            >
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading...
                </span>
              ) : (
                `Load More (${totalCount} total)`
              )}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}